import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import Status from "components/Status/Status";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import moment from "moment";
import { ProductionDateRange } from "Production/components/ProductionDateRange";
import { getTourFieldMetaData, getUpdateProductionInstruction, mapTourToState, TourFormState } from "shared/utils/fieldsHelper/tourFields";
import React, { useState } from "react";
import { CompanySidePanel } from "../Company/CompanySidePanel";
import { ProductionSidePanelShowList } from "../SidePanelShowList";
import { sidePanelFreeFieldSections } from "../SidePanelFreeFieldSection";
import { useHasMultipleStatuses } from "components/Status/hooks";
import { useIsInternal } from "common/SessionContext";

interface ProductionSidePanelDetailsProps {
  production: server.dto.Tour;
  freeFieldCategories: server.dto.FreeFieldCategorySmall[];
}

export const ProductionSidePanelDetails = (props: ProductionSidePanelDetailsProps) => {
  const isInternal = useIsInternal();
  const production = props.production;
  const hasMultipleStatuses = useHasMultipleStatuses("tour", production.clientId);

  const [formState, setFormState] = useState<TourFormState>(mapTourToState(production));

  React.useEffect(() => {
    if (props.production) {
      setFormState(mapTourToState(props.production));
    }
  }, [props.production]);

  const fieldMetaData = getTourFieldMetaData(site.currentEnvironment, formState, production.permissions, hasMultipleStatuses);

  const startMoment = production.startDate ? moment.utc(production.startDate) : null;
  const endMoment = production.endDate ? moment.utc(production.endDate) : null;

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      setFormState(prevState => ({
        ...prevState,
        name: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    }
  }

  return <SidePanelDetailSectionTemplate
    isInPageEditable={production.permissions.canModify}
    onValueChanged={onValueChanged}
    sections={[{
      key: "productionMain",
      type: "regular",
      hideTitle: true,
      title: site.currentEnvironment.tour.singular,
      fields: [{
        ...fieldMetaData.name.fields(),
        key: "name",
        label: RESX.GeneralLabels.Name,
        render: () => <div>{formState.name}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          initialValue: formState.name,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
            <RequiredTextInput
              validatorKey="name"
              focusOnMount
              maxLength={serverReference.constants.tour.maxNameLength}
              value={value}
              onChange={(name) => { onChange(name); }}
              onEnter={() => submit()}
              onEsc={() => cancel()}
            />
          ),
          submitChange: (name: string) => window.CoreApi.Production.Update(
            getUpdateProductionInstruction({ ...formState, name }))
        }
      }, {
        ...fieldMetaData.status.fields(),
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => <Status status={formState.status} />,
        inPageEdit: {
          render: ({ value, onChange }) => (
            <StatusDropdown
              value={value}
              onChange={onChange}
              mainArea="tour"
              clientId={production.clientId}
              options={undefined}
              config={{
                focusOnMount: true,
                openMenuOnFocus: true
              }}
            />
          ),
          type: "custom",
          initialValue: formState.status,
          fullWidth: true,
          submitChange: (status: server.dto.StatusSmall) => window.CoreApi.Production.Update(
            getUpdateProductionInstruction({ ...formState, status }))
        }
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        isEmpty: !production.startDate && !production.endDate,
        render: () => <ProductionDateRange startDate={startMoment} endDate={endMoment} />,
      }, {
        key: "artists",
        label: site.currentEnvironment.functionGroups.artist.plural,
        isEmpty: production.artists.length === 0,
        render: () => <>
          {production.artists.map(artist =>
            <div key={artist.artistId}>
              <CompanySidePanel type="artist"
                companyId={artist.artistId}
                trigger={<a>{artist.name}</a>}
              />
            </div>
          )}
        </>
      }]
    },
    ...sidePanelFreeFieldSections(props.freeFieldCategories, production.freeFields)]}
    additionalContent={<>
      <ProductionSidePanelShowList isInternal={isInternal} productionId={production.productionId} />
    </>}
  />
}
